import propertyService from '@/services/propertyService';

export const state = {
  properties: [],
  currentProperty: null,
  pagination: {
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0
  },
  loading: false,
  error: null
};

export const getters = {
  properties: (state) => state.properties,
  currentProperty: (state) => state.currentProperty,
  pagination: (state) => state.pagination,
  loading: (state) => state.loading,
  error: (state) => state.error,
  activeProperties: (state) => state.properties.filter(p => p.active),
  totalProperties: (state) => state.pagination.totalElements,
  hasMoreProperties: (state) => state.properties.length < state.pagination.totalElements
};

export const mutations = {
  SET_PROPERTIES(state, properties) {
    state.properties = properties;
  },
  APPEND_PROPERTIES(state, properties) {
    state.properties = [...state.properties, ...properties];
  },
  RESET_PROPERTIES(state) {
    state.properties = [];
    state.pagination = { page: 0, size: 10, totalElements: 0, totalPages: 0 };
  },
  SET_CURRENT_PROPERTY(state, property) {
    state.currentProperty = property;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = pagination;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  ADD_PROPERTY(state, property) {
    state.properties.unshift(property);
  },
  UPDATE_PROPERTY(state, updatedProperty) {
    const index = state.properties.findIndex(p => p.id === updatedProperty.id);
    if (index !== -1) {
      state.properties.splice(index, 1, updatedProperty);
    }
  },
  REMOVE_PROPERTY(state, propertyId) {
    state.properties = state.properties.filter(p => p.id !== propertyId);
  }
};

export const actions = {
  async fetchProperties({ commit }, { page = 0, size = 10, sort = 'name', direction = 'asc', append = false } = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      // response è PageResponse<PropertyDto> con: content, page, size, totalElements, totalPages, first, last
      const response = await propertyService.getProperties(page, size, sort, direction);
      if (append) {
        commit('APPEND_PROPERTIES', response.content || []);
      } else {
        commit('SET_PROPERTIES', response.content || []);
      }
      commit('SET_PAGINATION', {
        page: response.page ?? page,
        size: response.size ?? size,
        totalElements: response.totalElements ?? 0,
        totalPages: response.totalPages ?? 0
      });
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || error.message || 'Errore nel caricamento delle proprietà');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  resetProperties({ commit }) {
    commit('RESET_PROPERTIES');
  },

  async fetchProperty({ commit }, propertyId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const property = await propertyService.getProperty(propertyId);
      commit('SET_CURRENT_PROPERTY', property);
      return property;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Errore nel caricamento della proprietà');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createProperty({ commit }, propertyData) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const property = await propertyService.createProperty(propertyData);
      commit('ADD_PROPERTY', property);
      return property;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Errore nella creazione della proprietà');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateProperty({ commit }, { id, data }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const property = await propertyService.updateProperty(id, data);
      commit('UPDATE_PROPERTY', property);
      return property;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Errore nell\'aggiornamento della proprietà');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteProperty({ commit }, propertyId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      await propertyService.deleteProperty(propertyId);
      commit('REMOVE_PROPERTY', propertyId);
    } catch (error) {
      commit('SET_ERROR', error.message || 'Errore nell\'eliminazione della proprietà');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async toggleActive({ commit }, { id, active }) {
    try {
      const property = await propertyService.toggleActive(id, active);
      commit('UPDATE_PROPERTY', property);
      return property;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Errore nel cambio stato della proprietà');
      throw error;
    }
  },

  clearError({ commit }) {
    commit('SET_ERROR', null);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
